import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

// Simple in-app status message for the Paywall: a short message and an OK button in the same
// green as every other primary button. It replaces the native alert(), which is unstyled and
// froze the page before the new plan was painted. It is portalled to <body> so no parent's
// overflow or stacking context can clip it, and it works on any screen size (a centred card
// with a full-width OK on phones, an auto-width OK from the `sm` breakpoint up).
const PaywallNotice = ({ notice, onClose }) => {
  const { t } = useTranslation();
  const messageId = useId();
  const buttonRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const open = Boolean(notice);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    buttonRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      } else if (event.key === 'Tab') {
        // The only control is the OK button: keep focus on it while the dialog is open.
        event.preventDefault();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div
        role={notice.kind === 'error' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={messageId}
        aria-describedby={messageId}
        className="flex max-h-[90vh] w-full max-w-[420px] flex-col rounded-[12px] border border-[#E8EBE9] bg-white p-[24px] dark:border-[#5d625f] dark:bg-[#282a29]"
      >
        <div className="min-h-0 overflow-y-auto">
          <p
            id={messageId}
            className="break-words text-[16px] font-normal leading-relaxed text-[#282A29] dark:text-[#f0f5f2]"
          >
            {notice.message}
          </p>
        </div>
        <div className="mt-[24px] flex shrink-0 justify-end">
          <button
            ref={buttonRef}
            type="button"
            onClick={onClose}
            className="min-h-[44px] w-full rounded-[8px] bg-bloom-green px-[24px] py-[10px] text-[14px] font-medium text-white transition-colors hover:bg-bloom-green-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-bloom-green focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#282a29] sm:w-auto sm:min-w-[96px]"
          >
            {t('common.ok')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaywallNotice;
